const INTERESTED_RESPONSES = new Set([
  "Yes, definitely",
  "Yes, possibly",
  "I am interested for my child/young person",
]);

function responseDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function countResponseValues(rows, key) {
  return rows.reduce((counts, row) => {
    const values = Array.isArray(row[key]) ? row[key] : [row[key] || "No answer"];
    values.forEach((value) => {
      counts[value] = (counts[value] || 0) + 1;
    });
    return counts;
  }, {});
}

export function filterAiSurveyResponses(rows, filters) {
  const start = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`) : null;
  const end = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`) : null;
  return rows.filter((row) => {
    const submitted = responseDate(row.submittedAt);
    return (!filters.riverside || row.riversideResident === filters.riverside)
      && (!filters.area || row.area === filters.area)
      && (!filters.age || row.ageGroup === filters.age)
      && (!filters.interest || row.workshopInterest === filters.interest)
      && (!start || (submitted && submitted >= start))
      && (!end || (submitted && submitted <= end));
  });
}

export function topResponseLabels(rows, key, limit = 4) {
  return Object.entries(countResponseValues(rows, key))
    .filter(([label]) => label !== "No answer")
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([label]) => label);
}

export function getAiSurveyMetrics(rows) {
  const total = rows.length;
  const definitelyInterested = rows.filter((row) => row.workshopInterest === "Yes, definitely").length;
  const possiblyInterested = rows.filter((row) => row.workshopInterest === "Yes, possibly").length;
  const showingInterest = rows.filter((row) => INTERESTED_RESPONSES.has(row.workshopInterest)).length;
  const riversideRows = rows.filter((row) => row.riversideResident === "Yes");
  const riversideInterested = riversideRows.filter((row) => INTERESTED_RESPONSES.has(row.workshopInterest)).length;
  const contactRequested = rows.filter((row) => row.contactPermission === "Yes").length;
  const percentage = (value, denominator = total) => denominator ? Math.round((value / denominator) * 100) : 0;

  return {
    total,
    definitelyInterested,
    possiblyInterested,
    showingInterest,
    overallInterestPercentage: percentage(showingInterest),
    definitelyPercentage: percentage(definitelyInterested),
    possiblyPercentage: percentage(possiblyInterested),
    riversideResidents: riversideRows.length,
    riversideInterested,
    riversideInterestPercentage: percentage(riversideInterested, riversideRows.length),
    contactRequested,
  };
}

function naturalList(labels) {
  if (!labels.length) return "no workshop topics yet";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

export function createFundingEvidenceSummary(rows) {
  const metrics = getAiSurveyMetrics(rows);
  if (!metrics.total) return "No survey responses match the current filters yet.";
  const residentWord = metrics.total === 1 ? "resident has" : "residents have";
  const riversideWord = metrics.riversideResidents === 1 ? "respondent identified" : "respondents identified";
  const topics = naturalList(topResponseLabels(rows, "topics"));
  return `${metrics.total} local ${residentWord} responded to UUN's Community AI & Digital Skills Survey. ${metrics.definitelyPercentage}% said they would definitely attend local AI, coding or digital skills workshops and a further ${metrics.possiblyPercentage}% said they may attend. ${metrics.riversideResidents} ${riversideWord} themselves as Riverside Housing residents; ${metrics.riversideInterestPercentage}% of those respondents showed interest. The most requested subjects were ${topics}.`;
}
